import React, { ReactNode, useCallback, useState } from 'react';
import { MediaView } from 'common';
import { MediaType } from '@adalong/schemas';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import TextContent from './TextContent';
import { MediaImage } from '../MediaImage';
import UnknownStoryPreview from '../MediaUnknownStoryPreview/UnknownStoryPreview';
import translation from './translation';
import { style } from 'typestyle';

export interface MediaVisualState {
  failed: boolean;
}

export interface MediaVisualController extends MediaVisualState {
  loaded: boolean;
  onLoad: () => void;
  onError: () => void;
}

export function useMediaVisual(media: MediaView, onImageError?: () => void): MediaVisualController {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(media.isDeleted);
  const handleImageError = useCallback(() => {
    setFailed(true);
    onImageError?.();
  }, [onImageError]);

  return {
    failed,
    loaded,
    onLoad: () => setLoaded(true),
    onError: handleImageError,
  };
}

interface Props {
  media: MediaView;
  visual: MediaVisualController;
  children?: ReactNode | ((state: MediaVisualState) => ReactNode);
}

/** Shared format/error/video rendering used by complete cards and thumbnails. */
export function MediaVisual({ media, visual, children }: Props) {
  const failed = visual.failed ? lang.failed : undefined;

  return (
    <div className={mediaPreviewBodyClass} data-failed={failed}>
      {media.type === MediaType.Text ? (
        <TextContent caption={media.caption} />
      ) : media.type === MediaType.Untyped ? (
        <UnknownStoryPreview url={media.url.post} />
      ) : (
        <MediaImage src={media.cdn_image} media={media} onLoad={visual.onLoad} style={{ width: '100%' }} onError={visual.onError} />
      )}
      {media.type === 'video' && !failed && visual.loaded ? (
        <div className={playButtonClass} aria-hidden="true">
          <PlayArrowRoundedIcon className={faPlayClass} style={{ fontSize: '5rem' }} />
        </div>
      ) : null}
      {typeof children === 'function' ? children({ failed: failed !== undefined }) : children}
    </div>
  );
}

const lang = LocalizedStrings(translation);

const mediaPreviewBodyClass = style({
  position: 'relative',
  $nest: {
    '&[data-failed]': {
      minHeight: 150,
      $nest: {
        '&:before': {
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'absolute',
          top: 0,
          content: 'attr(data-failed)',
          color: '#8a786f',
          textAlign: 'center',
          height: '100%',
          width: '100%',
          left: 0,
        },
      },
    },
  },
});

const playButtonClass = style({
  display: 'flex',
  top: 0,
  left: 0,
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  zIndex: 1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  color: 'white',
});

const faPlayClass = style({
  margin: 'auto',
});

export default MediaVisual;
